
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import "./ViewRuleDatabase.css";
import searchruleIcon from "../../../common/assets/images/searchrule.png";
import testingruleIcon from "../../../common/assets/images/approvedIcon.png";
import archeiveruleIcon from "../../../common/assets/images/archeiverule.png";
import activeruleIcon from "../../../common/assets/images/activerule.png";
import totalruleIcon from "../../../common/assets/images/totalrule.png";
import archieveIcon from "../../../common/assets/images/archieveIcon.png";
import {
  fetchRuleDatabase, setSearch,
  setStatusFilter,
  setPage,
  setSelectedEditRuleId,
  resetFilters,
  moveRuleToArchive,
} from "./Redux/ruleDatabaseSlice";
import RefershIcon from "../../../common/assets/images/reload.png";
import { toast, ToastContainer } from "react-toastify";
import { Tooltip } from "@mui/material";
import PublishStatusModal from "../PublishedRule/PublishStatusModal";
import { getRuleIDStatusRepsone } from "../TestRule/TestRuleRedux";

const STATUS_OPTIONS = ["All Statuses", "Active", "Draft", "Archived", "Approved"];
const SEARCH_TYPE_OPTIONS = [
  { value: "rule_id", label: "Search by Rule ID" },
  { value: "description", label: "Search by Rule Description" },
  { value: "pathway_entry_point", label: "Search by Pathway Entry Point" },
];

function buildStats(cards) {
  return [
    { id: "total-rules", icon: totalruleIcon, label: "Total Rules", value: (cards.total_rules ?? 0).toLocaleString(), variant: "default" },
    {
      id: "active",
      icon: activeruleIcon,
      label: "Active",
      value: (cards.active_rules ?? 0).toLocaleString(),
      note: cards.total_rules > 0 ? `${Math.round((cards.active_rules / cards.total_rules) * 100)}% of total` : undefined,
      variant: "active",
    },
    { id: "archived", icon: archeiveruleIcon, label: "Archived", value: (cards.archived_rules ?? 0).toLocaleString(), variant: "default" },
    {
      id: "approved",
      icon: testingruleIcon,
      label: "Approved",
      value: (cards.approved_rules ?? 0).toLocaleString(),
      // badge: cards.testing_rules > 0 ? "Action needed" : undefined,
      variant: "testing",
    },
  ];
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const datePart = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart}, ${timePart} (UTC)`;

}

function StatCard({ icon, label, value, note, badge, variant }) {
  return (
    <div className={`viewRuleDb-stat viewRuleDb-stat--${variant}`}>
      <div className="viewRuleDb-stat-top">
        <span className="viewRuleDb-stat-label">{label}</span>
        <span className="viewRuleDb-stat-icon">
          {icon && <img src={icon} alt={label} />}
        </span>
      </div>
      <div className="viewRuleDb-stat-bottom">
        <span className="viewRuleDb-stat-value">{value}</span>
        {note && <span className="viewRuleDb-stat-note">{note}</span>}
        {badge && <span className="viewRuleDb-stat-badge">{badge}</span>}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const variant = (status || "").toLowerCase();
  return (
    <span className={`viewRuleDb-status viewRuleDb-status--${variant}`}>
      {status}
    </span>
  );
}

export default function ViewRuleDatabase() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { cards, data, hasMore, filters, loading, error } = useSelector(
    (state) => state.ruleDatabase
  );

  const [searchInput, setSearchInput] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const searchTimerRef = useRef(null);
  const [searchType, setSearchType] = useState("rule_id");
  const [showSearchTypeMenu, setShowSearchTypeMenu] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishModalType, setPublishModalType] = useState("loading");
  const [ruleStatusResponse, setRuleStatusResponse] = useState(null);
  // Close action menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (!e.target.closest(".viewRuleDb-actions-wrap")) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch whenever filters change (status, page, search, sort, etc.)
  useEffect(() => {
    dispatch(fetchRuleDatabase(filters));
  }, [dispatch, filters]);

  // Debounced search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      dispatch(setSearch({ type: searchType, value }));
    }, 400);
  };

  const handleStatusChange = (e) => {
    dispatch(setStatusFilter(e.target.value));
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const handleToggleMenu = (event, ruleId) => {
    if (openMenuId === ruleId) {
      closeMenu();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
    setOpenMenuId(ruleId);
  };

  const handleViewRule = (codeAssistManualRuleId) => {
    dispatch(setSelectedEditRuleId(codeAssistManualRuleId));
    navigate(`/view-active-rule`);
    closeMenu();
  };

  const handleEditRule = (codeAssistManualRuleId) => {
    dispatch(setSelectedEditRuleId(codeAssistManualRuleId));
    navigate(`/edit-active-rule`);
    closeMenu();
  };
  // NEW — Approved rules: View goes to the publish flow
  const handleViewApprovedRule = (codeAssistManualRuleId, rule) => {
    sessionStorage.setItem("environment-available", rule?.environment || "");
    const queryParams = new URLSearchParams({
      code_assist_manual_rule_id: codeAssistManualRuleId,
    });
    navigate(`/approved-rule-details?${queryParams.toString()}`);
    closeMenu();
  };
  // NEW — Archived rules: View goes to the archived-details page
  const handleViewArchivedRule = (codeAssistManualRuleId) => {
    const queryParams = new URLSearchParams({
      code_assist_manual_rule_id: codeAssistManualRuleId,
    });
    navigate(`/archived-rule-details?${queryParams.toString()}`);
    closeMenu();
  };
  const handleCreateNewRule = () => {
    navigate("/create-new-rule");
  };

  const handlePrevPage = () => {
    if (filters.page_number <= 1) return;
    dispatch(setPage(filters.page_number - 1));
  };

  const handleNextPage = () => {
    if (!hasMore) return;
    dispatch(setPage(filters.page_number + 1));
  };
  useEffect(() => {
    function handleClickOutside(e) {
      if (!e.target.closest(".viewRuleDb-actions-wrap")) {
        setOpenMenuId(null);
      }
      if (!e.target.closest(".viewRuleDb-search")) {
        setShowSearchTypeMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, [dispatch]);
  const handleSelectSearchType = (type) => {
     if (searchTimerRef.current) clearTimeout(searchTimerRef.current); // <-- add this
    setSearchType(type);
    setShowSearchTypeMenu(false);
    setSearchInput("");
    dispatch(setSearch({ type, value: "" })); // clear previous field's filter
  };
  const stats = buildStats(cards);
  const handleRefresh = () => {
    setSearchInput("");
    setSearchType("rule_id");
    setShowSearchTypeMenu(false);
    dispatch(resetFilters());
    // filters change -> the existing useEffect([dispatch, filters]) will refetch automatically
  };

   const wait = (ms) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  
    const pollRuleStatus = async (jobId) => {
    if (!jobId) {
      setRuleStatusResponse({
        items: [
          {
            status: "FAILED",
            error_message: "Job ID was not returned.",
          },
        ],
      });
      setPublishModalType("result");
      setShowPublishModal(true);
      return;
    }
  
    while (true) {
      try {
        const result = await dispatch(
          getRuleIDStatusRepsone(jobId)
        ).unwrap();
  
        console.log(result, "status API response");
  
        const items = result?.items || [];
  
        const isCompleted =
          items.length > 0 &&
          items.every(
            (item) =>
              item?.status === "COMPLETED" ||
              item?.status === "FAILED"
          );
  
        if (isCompleted) {
          setRuleStatusResponse(result);
          setPublishModalType("result");
          setShowPublishModal(true);
          return;
        }
  
        await wait(5000);
      } catch (error) {
        console.error("Status API failed:", error);
  
        setRuleStatusResponse({
          items: [
            {
              status: "FAILED",
              error_message: "Failed to fetch rule status.",
            },
          ],
        });
  
        setPublishModalType("result");
        setShowPublishModal(true);
        return;
      }
    }
  };
  
  const handleArchiveRule = async (codeAssistManualRuleId) => {
    closeMenu();
    setArchivingId(codeAssistManualRuleId);
    try {
      const result = await dispatch(
        moveRuleToArchive({ code_assist_manual_rule_id: codeAssistManualRuleId })
      ).unwrap();
    const jobId = result?.job_id;
    if (!jobId) {
      toast.error(
        "Rule archive request was successful, but the job ID was not returned."
      );
      return;
    }
    setRuleStatusResponse(null);
    setPublishModalType("loading");
    setShowPublishModal(true);
    pollRuleStatus(jobId);
    } catch (error) {
      toast.error(error || "Failed to archive the rule.");
    } finally {
      setArchivingId(null);
    }
  };
  return (
    <>
    <div className="viewRuleDb-page">
      <ToastContainer />
      {/* Header */}
      <div className="viewRuleDb-header">
        <div>
          <h1 className="viewRuleDb-title">
            Guidance &amp; Decision Support Rule Management
          </h1>
          <p className="viewRuleDb-subtitle">
            Manage and review configured guidance and decision support rules.
          </p>
        </div>
        <button type="button" className="viewRuleDb-create-btn" onClick={handleCreateNewRule}>
          <span className="viewRuleDb-create-plus">+</span> Create New Rule
        </button>
      </div>

      {/* Stat cards */}
      <div className="viewRuleDb-stats">
        {stats.map((stat) => (
          <StatCard key={stat.id} {...stat} />
        ))}
      </div>

      {/* Search + filter */}
      <div className="viewRuleDb-toolbar">
        <div className="viewRuleDb-search">
          <span className="viewRuleDb-search-icon" aria-hidden="true">
            <img alt="search" src={searchruleIcon} />
          </span>
          {/* <input
            type="text"
            className="viewRuleDb-search-input"
            placeholder="Search rules by ID, description, pathway, or code..."
            value={searchInput}
            onChange={handleSearchChange}
          /> */}
          <input
            type="text"
            className="viewRuleDb-search-input"
            placeholder={
              SEARCH_TYPE_OPTIONS.find((o) => o.value === searchType)?.label ||
              "Search rules..."
            }
            value={searchInput}
            onFocus={() => setShowSearchTypeMenu(true)}
            onClick={() => setShowSearchTypeMenu(true)}
            onChange={handleSearchChange}
          />

          {showSearchTypeMenu && (
            <div className="viewRuleDb-search-type-menu">
              {SEARCH_TYPE_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelectSearchType(option.value)}
                  className={`viewRuleDb-search-type-item ${option.value === searchType ? "viewRuleDb-search-type-item--active" : ""
                    }`}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <select
          className="viewRuleDb-status-select"
          value={filters.status.length ? filters.status[0] : "All Statuses"}
          onChange={handleStatusChange}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="viewRuleDb-refresh-btn"
          aria-label="Clear all filters and refresh"
          onClick={handleRefresh}
        >
          <img src={RefershIcon} alt="" />
        </button>
      </div>

      {/* Table */}
      <div className="viewRuleDb-table-wrap">
        <div className="viewRuleDb-table-scroll">
          <table className="viewRuleDb-table">
            <thead>
              <tr>
                <th>Rule ID</th>
                <th>Rule Description</th>
                <th>Pathway Entry Point</th>
                <th>Environment</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th className="viewRuleDb-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="viewRuleDb-empty">
                    <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                    Loading rules...
                  </td>
                </tr>
              )}

              {!loading && error && (
                <tr>
                  <td colSpan={7} className="viewRuleDb-empty">
                    Failed to load rules: {error}
                  </td>
                </tr>
              )}

              {!loading &&
                !error &&
                data.map((rule) => {
                  const entry = rule.pathway_entry?.[0] || {};
                  const env = Array.isArray(rule.environment) && rule.environment.length > 0
                    ? rule.environment.join(", ")
                    : "-";
                  return (
                    <tr
                      key={rule.code_assist_rule_id ?? rule.rule_id}
                      className={[
                        rule.status === "Archived" ? "viewRuleDb-row--archived" : "",
                        openMenuId === rule.rule_id ? "viewRuleDb-row--menu-open" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <td className="viewRuleDb-td-id">{rule.rule_id}</td>
                      <td className="viewRuleDb-td-desc">
                        {/* {rule.description} */}
                        <Tooltip title={rule.description || "No description provided"}>
                          <span>
                            {rule.description
                              ? rule.description.length > 20
                                ? `${rule.description.slice(0, 20)}...`
                                : rule.description
                              : "No description provided"}
                          </span>
                        </Tooltip>
                        </td>
                      <td>
                        <span className="viewRuleDb-pathway-tag">{entry.type}</span> 
                        {/* {entry.value} */}
                        <Tooltip title={entry.value || "No entry point"}>
                          <span>
                            {entry.value
                              ? entry.value.length > 20
                                ? `${entry.value.slice(0, 20)}...`
                                : entry.value
                              : "-"}
                          </span>
                        </Tooltip>
                      </td>
                      <td>{env}</td>
                      <td>
                        <StatusPill status={rule.status} />
                      </td>
                      <td>{formatDate(rule.updated_at)}</td>
                      <td className="viewRuleDb-td-actions">
                        <div className="viewRuleDb-actions-wrap">
                          <button
                            type="button"
                            className="viewRuleDb-action-btn"
                            aria-label={`Actions for ${rule.rule_id}`}
                            onClick={(e) => handleToggleMenu(e, rule.rule_id)}
                          >
                            &#8942;
                          </button>

                          {openMenuId === rule.rule_id && menuPosition && (
                            <div
                              className="viewRuleDb-action-menu"
                              style={{
                                top: menuPosition.top,
                                right: menuPosition.right,
                              }}
                            >
                              {(rule.status === "Active" || rule.status === "Draft") && (
                                <>
                                  <button
                                    type="button"
                                    className="viewRuleDb-action-menu-item"
                                    onClick={() => handleViewRule(rule.code_assist_manual_rule_id)}
                                  >
                                    <span className="viewRuleDb-action-menu-icon" aria-hidden="true">
                                      &#128065;
                                    </span>
                                    View
                                  </button>
                                  <button
                                    type="button"
                                    className="viewRuleDb-action-menu-item"
                                    onClick={() => handleEditRule(rule.code_assist_manual_rule_id)}
                                  >
                                    <span className="viewRuleDb-action-menu-icon" aria-hidden="true">
                                      &#9998;
                                    </span>
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="viewRuleDb-action-menu-item viewRuleDb-action-menu-item--danger"
                                    onClick={() => handleArchiveRule(rule.code_assist_manual_rule_id)}
                                  >
                                    <span className="viewRuleDb-action-menu-icon" aria-hidden="true">
                                      <img
                                        src={archieveIcon}
                                        alt=""
                                        className="viewRuleDb-action-menu-icon-img"
                                      />
                                    </span>
                                    Archive
                                  </button>
                                </>
                              )}
                              {rule.status === "Approved" && (
                                <button
                                  type="button"
                                  className="viewRuleDb-action-menu-item"
                                  onClick={() => {handleViewApprovedRule(rule.code_assist_manual_rule_id, rule)}}
                                >
                                  <span className="viewRuleDb-action-menu-icon" aria-hidden="true">&#128065;</span>
                                  View
                                </button>
                              )}

                              {rule.status === "Archived" && (
                                <button
                                  type="button"
                                  className="viewRuleDb-action-menu-item"
                                  onClick={() => handleViewArchivedRule(rule.code_assist_manual_rule_id)}
                                >
                                  <span className="viewRuleDb-action-menu-icon" aria-hidden="true">&#128065;</span>
                                  View
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

              {!loading && !error && data.length === 0 && (
                <tr>
                  <td colSpan={7} className="viewRuleDb-empty">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Footer / pagination */}
        <div className="viewRuleDb-footer">
          <span className="viewRuleDb-footer-count">
            {data.length === 0
              ? `Showing 0 of ${(cards.total_rules ?? 0).toLocaleString()} rules`
              : `Showing 1 to ${data.length} of ${(cards.total_rules ?? 0).toLocaleString()} rules`}
          </span>
          <div className="viewRuleDb-pagination">
            <button
              type="button"
              className="viewRuleDb-page-btn"
              disabled={filters.page_number <= 1}
              onClick={handlePrevPage}
            >
              Prev
            </button>
            <button
              type="button"
              className="viewRuleDb-page-btn"
              disabled={!hasMore}
              onClick={handleNextPage}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
    <PublishStatusModal
  show={showPublishModal}
  type={publishModalType}
  environment={[]}
  resultItems={ruleStatusResponse?.items || []}

  title="Rule Archive Information"
  loadingTitle="Archiving Rule"
  successTitle="Rule Archived Successfully"

  loadingMessage="Your rule is being moved to the archive."
  successMessage="Your rule has been successfully archived."

  loadingInfoMessage="Please do not close or refresh this page while the rule is being archived."

  failedTitle="Rule Archive Failed"
  failedMessage="We couldn't archive the rule."
  failedInfoMessage="Please review the details and try again."

  onClose={async () => {
    setShowPublishModal(false);

    if (publishModalType === "result") {
      await dispatch(fetchRuleDatabase(filters)).unwrap();
    }
  }}
/>
    </>
  );
}